const SELF_CLOSING = new Set(['br', 'hr', 'img', 'input', 'meta', 'link']);
const TAG_PATTERN = /<\/?([a-z][a-z0-9-]*)[^>]*>/gi;

/**
 * Strip orphan closing tags (e.g. `</b>` with no matching `<b>`) and auto-close any
 * unbalanced opens at the end. LLMs occasionally emit just a stray `</b>` at the
 * beginning of a response — without this, marked/DOMPurify may escape it and the
 * tag ends up visible as literal text.
 */
/**
 * Stateful tag balancer for streaming: keeps a running stack of open tags across chunks,
 * drops orphan closing tags (e.g. `</b>` with no matching open anywhere in the stream).
 * Usage: `const b = new StreamTagBalancer(); b.push(chunk1); b.push(chunk2); ...`
 */
export class StreamTagBalancer {
  private stack: string[] = [];

  reset(): void {
    this.stack = [];
  }

  push(chunk: string): string {
    let result = '';
    let last = 0;
    const re = /<\/?([a-z][a-z0-9-]*)[^>]*>/gi;
    let match: RegExpExecArray | null;
    while ((match = re.exec(chunk)) !== null) {
      const fullTag = match[0];
      const name = match[1].toLowerCase();
      const start = match.index;
      result += chunk.slice(last, start);
      if (SELF_CLOSING.has(name) || fullTag.endsWith('/>')) {
        result += fullTag;
      } else if (fullTag.startsWith('</')) {
        const idx = this.stack.lastIndexOf(name);
        if (idx !== -1) {
          this.stack.splice(idx, 1);
          result += fullTag;
        }
        // else: orphan closing tag — drop
      } else {
        this.stack.push(name);
        result += fullTag;
      }
      last = start + fullTag.length;
    }
    result += chunk.slice(last);
    return result;
  }
}

export function balanceHtmlTags(html: string): string {
  const stack: string[] = [];
  let result = '';
  let last = 0;
  TAG_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_PATTERN.exec(html)) !== null) {
    const fullTag = match[0];
    const name = match[1].toLowerCase();
    const start = match.index;
    result += html.slice(last, start);
    if (SELF_CLOSING.has(name) || fullTag.endsWith('/>')) {
      result += fullTag;
    } else if (fullTag.startsWith('</')) {
      const idx = stack.lastIndexOf(name);
      if (idx !== -1) {
        stack.splice(idx, 1);
        result += fullTag;
      }
      // else: drop orphan closing tag
    } else {
      stack.push(name);
      result += fullTag;
    }
    last = match.index + fullTag.length;
  }
  result += html.slice(last);
  while (stack.length) {
    result += `</${stack.pop()}>`;
  }
  return result;
}

/**
 * Drop a trailing partial open-tag so the browser parser doesn't swallow stray `<` or `</b` fragments.
 */
export function trimTrailingPartialTag(html: string): string {
  const lastOpen = html.lastIndexOf('<');
  const lastClose = html.lastIndexOf('>');
  if (lastOpen > lastClose) {
    return html.slice(0, lastOpen);
  }
  return html;
}

/**
 * Append closing tags for any unbalanced opened tags so mid-stream innerHTML is valid.
 */
export function closeUnbalancedTags(html: string): string {
  const tagPattern = /<\/?([a-z][a-z0-9-]*)[^>]*>/gi;
  const stack: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = tagPattern.exec(html)) !== null) {
    const fullTag = match[0];
    const name = match[1].toLowerCase();
    if (SELF_CLOSING.has(name) || fullTag.endsWith('/>')) continue;
    if (fullTag.startsWith('</')) {
      const idx = stack.lastIndexOf(name);
      if (idx !== -1) stack.splice(idx, 1);
    } else {
      stack.push(name);
    }
  }
  if (stack.length === 0) return html;
  return (
    html +
    stack
      .reverse()
      .map((t) => `</${t}>`)
      .join('')
  );
}

export function safeStreamingHtml(partial: string): string {
  return closeUnbalancedTags(trimTrailingPartialTag(partial));
}

/**
 * Compute how much of `partial` is safe to commit to the DOM — stops just before any
 * still-open trailing tag so the browser parser never sees a dangling `<` or `</b`.
 * `maxEnd` caps the result so per-frame chunks never extend past the typewriter budget.
 */
export function safeStreamEnd(partial: string, maxEnd: number = partial.length): number {
  const cap = Math.min(maxEnd, partial.length);
  const lastOpen = partial.lastIndexOf('<', cap - 1);
  const lastClose = partial.lastIndexOf('>', cap - 1);
  if (lastOpen > lastClose) return lastOpen;
  return cap;
}

const STREAM_TOKEN_RE = /<[^>]*>|&[a-zA-Z#0-9]+;|[\s\S]/g;

/**
 * Wrap every printable character / entity in the chunk with a fade-in span so the browser
 * animates their opacity. Tags, whitespace, and control chars are emitted verbatim so the
 * DOM keeps its structure (and blank lines don't get absorbed).
 */
export function wrapStreamChars(chunk: string): string {
  const tokens = chunk.match(STREAM_TOKEN_RE);
  if (!tokens) return '';
  const out: string[] = [];
  for (const tok of tokens) {
    if (tok.length > 1 && tok[0] === '<') {
      out.push(tok);
    } else if (tok === '\n' || tok === '\r' || tok === '\t' || tok === ' ') {
      out.push(tok);
    } else {
      // Add a tiny randomized animation-delay so the cadence reads as organic, not mechanical.
      const delay = Math.round(Math.random() * 40);
      out.push(`<span class="wc-stream-char" style="animation-delay:${delay}ms">${tok}</span>`);
    }
  }
  return out.join('');
}
